import type { z } from "zod";
import {
  ApiToken,
  Card,
  CardDetail,
  CardList,
  type CardStatus,
  type CatalogSource,
  EngineInfo,
  type EngineOverrides,
  Fetch,
  Images,
  Invited,
  InviteLink,
  Me,
  NewToken,
  Ok,
  Run,
  RunDetail,
  SearchResult,
} from "./models";

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type ZodFlat = { fieldErrors?: Record<string, string[]>; formErrors?: string[] };

function messageOf(body: unknown, status: number): string {
  const m = body && typeof body === "object" && "message" in body ? (body as { message: unknown }).message : null;
  if (typeof m === "string") return m;
  if (Array.isArray(m)) return m.map(String).join("; ");
  if (m && typeof m === "object") {
    const flat = m as ZodFlat;
    const parts = [
      ...(flat.formErrors ?? []),
      ...Object.entries(flat.fieldErrors ?? {}).map(([field, errs]) => `${field}: ${errs.join(", ")}`),
    ];
    if (parts.length) return parts.join("; ");
  }
  return `HTTP ${status}`;
}

let tokenProvider: () => Promise<string | undefined> = async () => undefined;

/** Called once at startup with the Firebase session's ID-token getter. */
export function setTokenProvider(provider: () => Promise<string | undefined>) {
  tokenProvider = provider;
}

export function onUnauthorized() {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== "/login") window.location.assign("/login");
}

export async function request<T>(
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = await tokenProvider();
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers["content-type"] = "application/json";
  const res = await fetch(`/api/${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, "Signed out");
  }
  if (!res.ok) throw new ApiError(res.status, messageOf(json, res.status));
  return schema.parse(json);
}

function query(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const me = () => request(Me, "GET", "auth/me");
export const acceptInvite = (token: string) => request(Invited, "POST", "invite/accept", { token });
export const regenerateInvite = () => request(InviteLink, "POST", "invite/regenerate");

export const listTokens = () => request(ApiToken.array(), "GET", "tokens");
export const createToken = (name: string) => request(NewToken, "POST", "tokens", { name });
export const revokeToken = (id: number) => request(Ok, "DELETE", `tokens/${id}`);

export interface ListCardsParams {
  status?: CardStatus;
  q?: string;
  page?: number;
  pageSize?: number;
}
export const listCards = (params: ListCardsParams) => request(CardList, "GET", `cards${query({ ...params })}`);
export const getCard = (id: number) => request(CardDetail, "GET", `cards/${id}`);

export interface Decision {
  status: CardStatus;
  productId?: string | null;
  identity?: Record<string, unknown> | null;
  sport?: string | null;
  cardFullName?: string | null;
  note?: string | null;
}
export const decide = (id: number, body: Decision) => request(Card, "PUT", `cards/${id}/decision`, body);
export const addByScan = (scanId: string) => request(Card, "POST", "cards/by-scan", { scanId });
/** `image` is a data URL. */
export const uploadPhoto = (image: string) => request(Card, "POST", "cards/upload", { image });

export const searchCatalog = (q: string, source: CatalogSource) =>
  request(SearchResult, "GET", `catalog/search${query({ q, source })}`);
export const catalogImages = (ids: string[]) =>
  request(Images, "GET", `catalog/images${query({ ids: ids.join(",") })}`);

export const listFetches = () => request(Fetch.array(), "GET", "fetches");
export const getFetch = (id: number) => request(Fetch, "GET", `fetches/${id}`);
export interface FetchParams {
  count?: number;
  minPage?: number;
  maxPage?: number;
  perPage?: number;
  seed?: number;
}
export const startFetch = (params: FetchParams) => request(Fetch, "POST", "fetches", params);

export const listRuns = () => request(Run.array(), "GET", "runs");
export const getEngine = () => request(EngineInfo, "GET", "runs/engine");
export const getRun = (id: number) => request(RunDetail, "GET", `runs/${id}`);
export interface RunParams {
  statuses?: CardStatus[];
  cardIds?: number[];
  limit?: number;
  engine?: EngineOverrides;
}
export const startRun = (params: RunParams) => request(Run, "POST", "runs", params);
